import { useCallback, useEffect, useState } from "react";

export type FavoriteType = "news" | "works" | "characters";

export type FavoriteEntry = {
  type: FavoriteType;
  id: string;
  title: string;
  savedAt: string;
};

const STORAGE_KEY = "age-anime-favorites";
const SYNC_EVENT = "age-favorites-updated";

const readFavorites = (): FavoriteEntry[] => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((item) => item && typeof item.id === "string" && typeof item.type === "string");
  } catch {
    return [];
  }
};

const writeFavorites = (list: FavoriteEntry[]) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch {
    // storage may be unavailable; state still updates in memory
  }
  window.dispatchEvent(new Event(SYNC_EVENT));
};

export function useFavorites() {
  const [favorites, setFavorites] = useState<FavoriteEntry[]>([]);

  useEffect(() => {
    setFavorites(readFavorites());
    const sync = () => setFavorites(readFavorites());
    window.addEventListener(SYNC_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(SYNC_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const toggleFavorite = useCallback((entry: { type: FavoriteType; id: string; title: string }) => {
    const current = readFavorites();
    const exists = current.some((item) => item.type === entry.type && item.id === entry.id);
    const next = exists
      ? current.filter((item) => !(item.type === entry.type && item.id === entry.id))
      : [{ ...entry, savedAt: new Date().toISOString() }, ...current];
    writeFavorites(next);
    return !exists;
  }, []);

  const isFavorite = useCallback(
    (type: FavoriteType, id: string) => favorites.some((item) => item.type === type && item.id === id),
    [favorites],
  );

  return { favorites, toggleFavorite, isFavorite };
}